interface PerformanceMark {
  name: string
  startTime: number
  endTime?: number
  duration?: number
  source?: "server" | "client"
}

interface PerformanceSummary {
  measurements: PerformanceMark[]
  totalTime: number
  segments: number
  perfilsCount: number
  timestamp: number
}

class EnhancedPerformanceMonitor {
  private marks: Map<string, PerformanceMark> = new Map()
  private enabled: boolean = process.env.NODE_ENV === "development"
  private isClient: boolean = typeof window !== "undefined"
  private pageData: { segments: number; perfilsCount: number } = { segments: 0, perfilsCount: 0 }

  setPageData(segments: number, perfilsCount: number) {
    this.pageData = { segments, perfilsCount }
  }

  // Add server measurements to client-side monitor
  addServerMeasurement(name: string, duration: number) {
    if (!this.isClient) return

    this.marks.set(name, {
      name,
      startTime: 0,
      endTime: duration,
      duration,
      source: "server",
    })
  }

  startMark(name: string) {
    if (!this.enabled) return

    const startTime = performance.now()
    this.marks.set(name, {
      name,
      startTime,
      source: this.isClient ? "client" : "server",
    })

    // Server-side: Simple logging
    if (!this.isClient) {
      console.log(`🚀 Starting: ${name}`)
    }
  }

  endMark(name: string) {
    if (!this.enabled) return

    const mark = this.marks.get(name)
    if (!mark) return

    const endTime = performance.now()
    const duration = endTime - mark.startTime

    mark.endTime = endTime
    mark.duration = duration

    // Server-side: Keep your current format
    if (!this.isClient) {
      if (duration > 100) {
        console.log(`🐌 Slow operation: ${name} took ${duration.toFixed(2)}ms`)
      } else if (duration > 50) {
        console.log(`⚠️ ${name} took ${duration.toFixed(2)}ms`)
      } else {
        console.log(`✅ ${name} took ${duration.toFixed(2)}ms`)
      }
    }

    return duration
  }

  getMeasurements(): PerformanceMark[] {
    return Array.from(this.marks.values()).filter((m) => m.duration !== undefined)
  }

  getPerformanceSummary(): PerformanceSummary {
    const measurements = this.getMeasurements()
    const totalTime = measurements.find((m) => m.name === "SegmentsPage-total")?.duration || 0

    return {
      measurements,
      totalTime,
      segments: this.pageData.segments,
      perfilsCount: this.pageData.perfilsCount,
      timestamp: Date.now(),
    }
  }

  // Get server data for passing to client
  getServerData() {
    if (this.isClient) return null

    const measurements = this.getMeasurements()
    return {
      measurements: measurements.map((m) => ({
        name: m.name,
        duration: m.duration || 0,
      })),
      totalTime: measurements.find((m) => m.name === "SegmentsPage-total")?.duration || 0,
    }
  }

  // Client-side organized summary
  logClientSummary() {
    if (!this.isClient || !this.enabled) return

    const summary = this.getPerformanceSummary()

    console.group("🔍 Feed Page Performance Summary")
    console.log(`📊 Total page load time: ${summary.totalTime.toFixed(2)}ms`)
    console.log(`🔢 Segments processed: ${summary.segments}`)
    console.log(`👥 Profiles loaded: ${summary.perfilsCount}`)
    console.log("")
    console.log("📈 Detailed Measurements:")

    summary.measurements
      .filter((m) => m.duration && m.duration > 0)
      .sort((a, b) => (b.duration || 0) - (a.duration || 0))
      .forEach((m) => {
        const duration = m.duration || 0
        const emoji = duration > 100 ? "🔴" : duration > 50 ? "🟡" : "🟢"
        const source = m.source === "server" ? "🖥️" : "💻"
        console.log(`${emoji} ${source} ${m.name}: ${duration.toFixed(2)}ms`)
      })

    console.groupEnd()

    // Optional: Performance analytics
    this.sendToAnalytics(summary)
  }

  private sendToAnalytics(summary: PerformanceSummary) {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "page_performance", {
        event_category: "Performance",
        event_label: "Feed Page",
        value: Math.round(summary.totalTime),
        segments_count: summary.segments,
        profiles_count: summary.perfilsCount,
      })
    }
  }

  // Reset for new page loads
  reset() {
    this.marks.clear()
    this.pageData = { segments: 0, perfilsCount: 0 }
  }
}

export const perfMonitorEnhanced = new EnhancedPerformanceMonitor()

// Export for backward compatibility
export const perfMonitor = perfMonitorEnhanced
